/**
 * PATH: packages/job-hub/src/jobs/qa/itpSignoff.ts
 * FILE_ID: JOBHUB-QA-ITP-SIGNOFF-001
 * PURPOSE: The hold point sign-off line printed on an ITP check card.
 * WHY: itpDocument.ts is near the 500-line ceiling. The signature image uses
 *      the token link from the payload, so Print and the server PDF both load
 *      it without a session.
 */
import { qaItemSignoff } from './qaHoldPoints'
import type { QaHoldPoint, QaItemInstance } from './qaTypes'

export interface ItpSignoff {
  state: 'needed' | 'requested' | 'approved' | 'rejected'
  signerName?: string
  signerRole?: string
  signedAt?: string
  signatureUrl?: string
  rejectedBy?: string
  comment?: string
}

export const ITP_SIGNOFF_CSS =
  '.signoff { display:flex; flex-wrap:wrap; align-items:center; gap:8px; margin:0 0 6px; font-size:11px; }' +
  ' .signoff img { height:36px; width:auto; }' +
  ' .signoff-open { color:#b45309; } .signoff-rejected { color:#dc2626; }'

function esc(value: string): string {
  return value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;')
}

export function itpSignoffFor(
  item: Pick<QaItemInstance, 'id' | 'hold_point_config'>,
  holdPoints: readonly QaHoldPoint[] | undefined,
): ItpSignoff | undefined {
  const { state, holdPoint, signature } = qaItemSignoff(item, holdPoints)
  if (state === 'none') return undefined
  if (state === 'approved') {
    return {
      state,
      signerName: signature?.signer_name || holdPoint?.approved_by_name || undefined,
      signerRole: signature?.signer_role || undefined,
      signedAt: signature?.signed_at || holdPoint?.approved_at || undefined,
      signatureUrl: signature?.signature_url || undefined,
    }
  }
  if (state === 'rejected') {
    return {
      state,
      rejectedBy: holdPoint?.rejected_by_name || undefined,
      comment: holdPoint?.decision_comment || undefined,
    }
  }
  return { state }
}

export function itpSignoffHtml(signoff: ItpSignoff | undefined): string {
  if (!signoff) return ''
  if (signoff.state === 'approved') {
    const who = [signoff.signerName, signoff.signerRole].filter(Boolean).join(', ')
    const when = signoff.signedAt ? signoff.signedAt.slice(0, 10) : ''
    const text = ['Hold point signed', who, when].filter(Boolean).join(' · ')
    const image = signoff.signatureUrl ? `<img src="${esc(signoff.signatureUrl)}" alt="Signature" />` : ''
    return `<div class="signoff"><span>${esc(text)}</span>${image}</div>`
  }
  if (signoff.state === 'rejected') {
    const text = ['Hold point sign-off rejected', signoff.rejectedBy, signoff.comment].filter(Boolean).join(' · ')
    return `<div class="signoff signoff-rejected">${esc(text)}</div>`
  }
  return '<div class="signoff signoff-open">Hold point: awaiting sign-off</div><div class="signoff-line">Signature: ____________</div>'
}
